import { TRIAL_DAYS } from '@/data/constants';

/**
 * The duration of the trial in days (FOR-2710, stage 2g): the public setting `trial.durationDays` of the service.
 * The site takes the number from the setting on the server (the pages are rendered there) and shows the same number in all the places.
 */
const apiUrl = process.env.API_URL;
const KEY = 'trial.durationDays';
const CACHE_MS = 60_000;
const TIMEOUT_MS = 2_000;

let cache: { days: number, expiresAt: number } | undefined;

/**
 * The number of the days of the trial from the settings. The answer is kept for a minute; when the service does not answer
 * (or the value is wrong) the last known value (or the default one, 7) is returned, so that a page is never broken by it.
 */
export const getTrialDays = async (): Promise<number> => {
  if (cache && cache.expiresAt > Date.now()) return cache.days;

  const fallback = cache?.days ?? TRIAL_DAYS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${apiUrl}/settingspublic`, { signal: controller.signal, cache: 'no-store' });
    if (!response.ok) throw new Error(String(response.status));
    const value = (await response.json())?.[KEY];
    const days = typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : fallback;
    cache = { days, expiresAt: Date.now() + CACHE_MS };
    return days;
  } catch {
    cache = { days: fallback, expiresAt: Date.now() + CACHE_MS / 4 };
    return fallback;
  } finally {
    clearTimeout(timer);
  }
};

/** The days with the right form of the word in Russian: "7 дней", "3 дня", "1 день", "21 день" */
export const trialDaysText = (days: number) => {
  const mod100 = days % 100;
  const mod10 = days % 10;
  let word = 'дней';
  if (mod100 < 11 || mod100 > 14) {
    if (mod10 === 1) word = 'день';
    else if (mod10 >= 2 && mod10 <= 4) word = 'дня';
  }
  return `${days} ${word}`;
};

/** The placeholder of the data files (public/data/*): it is replaced by the days with the word ("7 дней") */
export const TRIAL_DAYS_PLACEHOLDER = '{{trialDays}}';

/** Replaces the placeholder in all the strings of the data (the content of the pages is kept in the JSON files) */
export const replaceTrialDays = <T>(data: T, days: number): T => {
  const text = trialDaysText(days);
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') return value.includes(TRIAL_DAYS_PLACEHOLDER) ? value.replaceAll(TRIAL_DAYS_PLACEHOLDER, text) : value;
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, walk(item)]));
    return value;
  };
  return walk(data) as T;
};
